import { defineStore } from 'pinia';
import { getProductNames } from '@base-shared/utils';
import type { APP, SubscriptionAlertMilestone, SubscriptionProductScope } from '@base-shared/utils/constants';
import type { components } from '@base-shared/types/group.openapi';
import type { FeatureSetAction } from '@stores/player-feature-set-actions';
import type { FileUploadItem } from '@base-shared/types/file-uploader';

/**
 * Local storage keys used in the application.
 */
export const HUB_SIDEBAR_COLLAPSED = 'HUB_SIDEBAR_COLLAPSED';
export const HUB_SIGN_UP_SUCCESS_INFO = 'HUB_SIGN_UP_SUCCESS_INFO';
export const FEATURE_SETS_ACTIONS_QUEUE = 'FEATURE_SETS_ACTIONS_QUEUE';
export const HUB_SUBSCRIPTION_ALERTS = 'HUB_SUBSCRIPTION_ALERTS';
export const LIST_VIEW_MODE_PREFERENCES = 'LIST_VIEW_MODE_PREFERENCES';
export const LIST_VIEW_CONTEXT_MEDIA = 'media';
export const LIST_VIEW_CONTEXT_PLAYERS = 'players';
export const LIST_VIEW_CONTEXT_SCHEDULE = 'schedule';
const IS_MIGRATING_FROM_SOCIAL_ACCOUNT = 'isMigratingFromSocialAccount';
export const ARYA_FILE_UPLOADS = 'aryaFileUploads';
export const ARYA_MEDIA_V2_ENABLED = 'ARYA_MEDIA_V2_ENABLED';
export const ARYA_MEDIA_RESOLUTIONS = 'ARYA_MEDIA_RESOLUTIONS';
export const RESOLUTIONS_CONTEXT_FILES = 'files';
export const RESOLUTIONS_CONTEXT_LED = 'led';
export const RESOLUTIONS_CONTEXT_VIDEOWALL = 'videowall';
export const RESOLUTIONS_CONTEXT_ALERTS = 'alerts';

export interface HubSignUpSuccessInfo {
  app: APP | null;
  productsEnabled: components['schemas']['Product'][] | null;
};

/**
 * Data structure for storing subscription alerts in local storage
 * per group. The key is the group identifier (e.g., group ID).
 */
export type HubSubscriptionAlert = {
  subscription: SubscriptionProductScope;
  groupId: string;
  lastDismissedMilestone: SubscriptionAlertMilestone;
  dismissedAt: string;
  expirationDate: string;
};

/**
 * Data structure for storing the media list view mode in
 * local storage.
 * This will be used for Media (Files, LED, Videowall, Alerts),
 * Players, and Schedule pages to persist the user's preferred
 * view mode across sessions.
 */
export type ListViewMode = 'grid' | 'list' | null;
export type ListViewContext = typeof LIST_VIEW_CONTEXT_MEDIA | typeof LIST_VIEW_CONTEXT_PLAYERS | typeof LIST_VIEW_CONTEXT_SCHEDULE;
export type ListViewModePreferences = {
  [key in ListViewContext]?: ListViewMode;
};

/**
 * Data structure for storing the media list view mode in
 * local storage.
 * This will be used for Media (Files, LED, Videowall, Alerts),
 * Players, and Schedule pages to persist the user's preferred
 * view mode across sessions.
 */
export type MediaResolution = 'horizontal' | 'vertical' | string | null;
export type MediaResolutionContext = typeof RESOLUTIONS_CONTEXT_FILES | typeof RESOLUTIONS_CONTEXT_LED | typeof RESOLUTIONS_CONTEXT_VIDEOWALL | typeof RESOLUTIONS_CONTEXT_ALERTS;
export type MediaResolutionsPreferences = {
  [key in MediaResolutionContext]?: MediaResolution;
};

interface LocalStorageState {
  isSideNavCollapsed: boolean | null;
  hubSignUpSuccessInfo: HubSignUpSuccessInfo | null;
  listViewModePreferences: ListViewModePreferences;
  isAryaMediaV2Enabled: boolean;
  aryaFileUploads: FileUploadItem[];
  aryaMediaResolutions: MediaResolutionsPreferences;
};

const isFeatureSetAction = (value: unknown): value is FeatureSetAction => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<FeatureSetAction>;
  return typeof candidate.playerSerialNumber === 'string'
    && typeof candidate.operation === 'string'
    && typeof candidate.featureSetType === 'string'
    && typeof candidate.expiresAt === 'number';
};

export const useLocalStorageStore = defineStore('localStorage', {
  state: (): LocalStorageState => ({
    isSideNavCollapsed: false,
    hubSignUpSuccessInfo: null,
    listViewModePreferences: {},
    isAryaMediaV2Enabled: false,
    aryaFileUploads: [],
    aryaMediaResolutions: {}
  }),
  getters: {
    hubSignUpSuccessProductsEnabled: (state) => {
      return state.hubSignUpSuccessInfo?.productsEnabled
        ? getProductNames(state.hubSignUpSuccessInfo.productsEnabled)?.join(', ')
        : null;
    }
  },
  actions: {
    /**
     * Utility functions to manage sidebar collapse state in local storage.
     * This is used to persist the sidebar state across page reloads.
     */
    clearSidebarCollapseToStorage() {
      localStorage.removeItem(HUB_SIDEBAR_COLLAPSED);
      this.isSideNavCollapsed = null;
    },
    getSidebarCollapseFromStorage() {
      const savedState = localStorage.getItem(HUB_SIDEBAR_COLLAPSED);

      this.isSideNavCollapsed = savedState !== null ? savedState === 'true' : null;
      return this.isSideNavCollapsed;
    },
    setSidebarCollapseInStorage(collapsed: boolean) {
      localStorage.setItem(HUB_SIDEBAR_COLLAPSED, collapsed.toString());
      this.isSideNavCollapsed = collapsed;
    },

    /**
     * Utility functions to manage sign-up success information in local storage.
     * This is used to store the app and products enabled after a successful sign-up.
     * It allows the application to redirect the user to the appropriate page after sign-up.
     */
    clearSignUpSuccessInfoToStorage() {
      localStorage.removeItem(HUB_SIGN_UP_SUCCESS_INFO);
      this.hubSignUpSuccessInfo = null;
    },
    getSignUpSuccessInfoFromStorage(): HubSignUpSuccessInfo | null {
      const savedState = localStorage.getItem(HUB_SIGN_UP_SUCCESS_INFO);
      if (!savedState) return null;

      try {
        this.hubSignUpSuccessInfo = JSON.parse(savedState);
        return this.hubSignUpSuccessInfo;
      } catch {
        return null;
      }
    },
    setSignUpSuccessInfoToStorage(hubSignUpSuccessInfo: HubSignUpSuccessInfo) {
      if (!hubSignUpSuccessInfo?.app) {
        // Reset signUpSuccessInfo if no app is provided
        this.clearSignUpSuccessInfoToStorage();
        return;
      }

      const jsonString = JSON.stringify(hubSignUpSuccessInfo);
      localStorage.setItem(HUB_SIGN_UP_SUCCESS_INFO, jsonString);
      this.hubSignUpSuccessInfo = hubSignUpSuccessInfo;
    },

    /**
     * Assigns a feature set to a player and stores the action in localStorage.
     * NOTE: Pinia store is not used intentionally to avoid reactivity overhead.
     */
    updateFeatureSetActions(actions: FeatureSetAction[]): void {
      try {
        localStorage.setItem(FEATURE_SETS_ACTIONS_QUEUE, JSON.stringify(actions));
      } catch {
        console.error('Failed to store feature set action in localStorage.');
      }
    },

    /**
     * Retrieves feature set actions from localStorage, filtering out expired actions.
     * NOTE: Pinia store is not used intentionally to avoid reactivity overhead.
     * @returns Array of valid FeatureSetAction objects without expired records.
     */
    getFeatureSetActionsFromStorage(): FeatureSetAction[] {
      const raw = localStorage.getItem(FEATURE_SETS_ACTIONS_QUEUE);
      if (!raw) return [];

      try {
        const parsed = JSON.parse(raw) as unknown[];
        return parsed.filter(isFeatureSetAction);
      } catch {
        console.error('Failed to parse feature set actions from localStorage.');
        return [];
      }
    },

    clearFeatureSetActionsFromStorage(): void {
      localStorage.removeItem(FEATURE_SETS_ACTIONS_QUEUE);
    },

    /**
     * Utility functions to manage subscription alerts in local storage.
     * This is used to persist the dismissed subscription alerts across page reloads.
     * Group-specific alerts are stored using the group ID as the key.
     */
    clearSubscriptionAlerts() {
      localStorage.removeItem(HUB_SUBSCRIPTION_ALERTS);
    },
    getSubscriptionAlertsFromStorage(): HubSubscriptionAlert[] | null {
      const savedState = localStorage.getItem(HUB_SUBSCRIPTION_ALERTS);
      if (!savedState) return null;

      try {
        return JSON.parse(savedState);
      } catch {
        return null;
      }
    },
    setSubscriptionAlertsToStorage(alerts: HubSubscriptionAlert[]) {
      try {
        if (!alerts || alerts.length === 0) {
          this.clearSubscriptionAlerts();
          return;
        }

        localStorage.setItem(HUB_SUBSCRIPTION_ALERTS, JSON.stringify(alerts));
      } catch {
        console.error('Failed to store subscription alert localStorage.');
      }
    },

    /**
     * Utility functions to manage list view mode preferences in local storage.
     * This is used to persist the user's preferred view mode (grid or list) for
     * Media, Players, and Schedule pages across sessions.
     */
    clearListViewModePreferencesFromStorage() {
      localStorage.removeItem(LIST_VIEW_MODE_PREFERENCES);
      this.listViewModePreferences = {};
    },
    getListViewModePreferencesFromStorage(): ListViewModePreferences {
      const savedState = localStorage.getItem(LIST_VIEW_MODE_PREFERENCES);
      if (!savedState) {
        this.listViewModePreferences = {};
        return {};
      }

      try {
        this.listViewModePreferences = JSON.parse(savedState);
        return this.listViewModePreferences;
      } catch {
        this.listViewModePreferences = {};
        return {};
      }
    },
    getListViewModePreferenceForContext(context: ListViewContext): ListViewMode {
      const currentConfig = this.getListViewModePreferencesFromStorage();
      return currentConfig[context] || null;
    },
    setListViewModePreferencesToStorage(config: ListViewModePreferences) {
      try {
        if (!config || Object.keys(config).length === 0) {
          this.clearListViewModePreferencesFromStorage();
          this.listViewModePreferences = {};
          return;
        }

        localStorage.setItem(LIST_VIEW_MODE_PREFERENCES, JSON.stringify(config));
        this.listViewModePreferences = config;
      } catch {
        this.listViewModePreferences = config;
        console.error('Failed to store list view mode preferences in localStorage.');
      }
    },
    setListViewModePreferenceForContext(context: ListViewContext, mode: ListViewMode) {
      const currentConfig = this.getListViewModePreferencesFromStorage();
      currentConfig[context] = mode;
      this.setListViewModePreferencesToStorage(currentConfig);
    },

    /**
     * Utility functions to manage the Arya Media V2 feature toggle in local storage.
     * This is used to persist the user's preference for using the new media experience.
     */
    clearAryaMediaV2EnabledFromStorage() {
      localStorage.removeItem(ARYA_MEDIA_V2_ENABLED);
      this.isAryaMediaV2Enabled = false;
    },
    getAryaMediaV2EnabledFromStorage(): boolean {
      const savedState = localStorage.getItem(ARYA_MEDIA_V2_ENABLED);
      this.isAryaMediaV2Enabled = savedState === 'true';
      return this.isAryaMediaV2Enabled;
    },
    setAryaMediaV2EnabledToStorage(enabled: boolean) {
      localStorage.setItem(ARYA_MEDIA_V2_ENABLED, enabled.toString());
      this.isAryaMediaV2Enabled = enabled;
    },

    /**
     * Utility functions to manage the auth migration state
     * from social account in local storage.
     */
    setIsMigratingFromSocialAccountToStorage(provider: string, isMigrating: boolean) {
      localStorage.setItem(IS_MIGRATING_FROM_SOCIAL_ACCOUNT, JSON.stringify({ provider, isMigrating }));
    },
    getIsMigratingFromSocialAccountFromStorage() {
      const savedState = localStorage.getItem(IS_MIGRATING_FROM_SOCIAL_ACCOUNT);
      if (!savedState) return null;

      return JSON.parse(savedState);
    },
    clearIsMigratingFromSocialAccountFromStorage() {
      localStorage.removeItem(IS_MIGRATING_FROM_SOCIAL_ACCOUNT);
    },

    /**
     * Utility functions to manage Arya file uploads in local storage.
     * This is used to persist the list of file uploads across sessions.
     */
    clearAryaFileUploadsFromStorage() {
      localStorage.removeItem(ARYA_FILE_UPLOADS);
      this.aryaFileUploads = [];
    },
    getAryaFileUploadsFromStorage(): FileUploadItem[] {
      try {
        const raw = localStorage.getItem(ARYA_FILE_UPLOADS);
        this.aryaFileUploads = raw
          ? (JSON.parse(raw) as FileUploadItem[]).filter(Boolean)
          : [];
        return this.aryaFileUploads;
      } catch {
        this.aryaFileUploads = [];
        return [];
      }
    },
    setAryaFileUploadsToStorage(items: FileUploadItem[]): void {
      localStorage.setItem(ARYA_FILE_UPLOADS, JSON.stringify(items));
      this.aryaFileUploads = items;
    },

    /**
     * Utility functions to manage list view mode preferences in local storage.
     * This is used to persist the user's preferred view mode (grid or list) for
     * Media, Players, and Schedule pages across sessions.
     */
    clearAryaMediaResolutionsFromStorage() {
      localStorage.removeItem(ARYA_MEDIA_RESOLUTIONS);
      this.aryaMediaResolutions = {};
    },
    getAryaMediaResolutionsFromStorage(): MediaResolutionsPreferences {
      const savedState = localStorage.getItem(ARYA_MEDIA_RESOLUTIONS);
      if (!savedState) {
        this.aryaMediaResolutions = {};
        return {};
      }

      try {
        this.aryaMediaResolutions = JSON.parse(savedState);
        return this.aryaMediaResolutions;
      } catch {
        this.aryaMediaResolutions = {};
        return {};
      }
    },
    getAryaMediaResolutionForContext(context: MediaResolutionContext): MediaResolution {
      const currentConfig = this.getAryaMediaResolutionsFromStorage();
      return currentConfig[context] || null;
    },
    setAryaMediaResolutionsToStorage(config: MediaResolutionsPreferences) {
      try {
        if (!config || Object.keys(config).length === 0) {
          this.clearAryaMediaResolutionsFromStorage();
          this.aryaMediaResolutions = {};
          return;
        }

        localStorage.setItem(ARYA_MEDIA_RESOLUTIONS, JSON.stringify(config));
        this.aryaMediaResolutions = config;
      } catch {
        this.aryaMediaResolutions = config;
        console.error('Failed to store Arya media resolutions in localStorage.');
      }
    },
    setAryaMediaResolutionForContext(context: MediaResolutionContext, resolution: MediaResolution) {
      const currentConfig = this.getAryaMediaResolutionsFromStorage();
      currentConfig[context] = resolution;
      this.setAryaMediaResolutionsToStorage(currentConfig);
    },

    /**
     * Resets local storage used in the application.
     */
    resetLocalStorage() {
      this.clearSidebarCollapseToStorage();
      this.clearSignUpSuccessInfoToStorage();
      this.clearFeatureSetActionsFromStorage();
      this.clearSubscriptionAlerts();
      this.clearAryaFileUploadsFromStorage();
    }
  }
});
